import React, { useEffect, useState } from "react";
import {
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { zipCodes } from "../../../data/zipcodes";
import { useAppState } from "../../../appContext";
import { useFilterContext } from "@/appContext/FilterContext";

const defaultNY = {
  lat: 40.809947662368856,
  lng: -73.22082853735371,
};

const defaultLocationValue = {
  miles: 20,
  locationType: "default",
  zip: "",
  latitude: null,
  longitude: null,
  isValid: false,
  message: "",
};

// Helper function to calculate distance between two points using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;
  return distance;
};

function LocationFilter({ onUpdate, _value = [] }) {

  const [state] = useAppState();
  const [isHydrated, setIsHydrated] = useState(false);
  const [defaultPosition, setDefaultPosition] = useState(defaultNY);
  const [localValue, setLocalValue] = useState(defaultLocationValue);
  const [debugInfo, setDebugInfo] = useState("");
  const { saveAppliedFilter, setSaveAppliedFilter, setRemoveAppliedFilter } =
    useFilterContext();

  useEffect(() => {
    setIsHydrated(true);

    // Load stored position on client mount
    const storedPosition = JSON.parse(
      localStorage.getItem("geo_position") || "null"
    );
    setDefaultPosition(storedPosition || defaultNY);

    // Load stored location filter on client mount
    const storedLocationFilter = localStorage.getItem("location_filter");
    if (storedLocationFilter) {
      setLocalValue(JSON.parse(storedLocationFilter));
    }
  }, []);

  const getMissingZips = () => {
    const zipSet = new Set(zipCodes.map((zip) => zip.zipcode));
    const missingZips = state?.venues
      ?.filter((venue) => !zipSet.has(venue.VENUE_ZIP))
      ?.map((venue) => ({
        zipcode: venue.VENUE_ZIP,
        lat: venue.lat,
        lon: venue.lon,
      }));
    return missingZips || [];
  };

  const findCoordinatesByZip = (zip) => {
    const zipEntry = [...zipCodes, ...getMissingZips()].find(
      (z) => z.zipcode + "" === zip
    );
    return zipEntry
      ? { latitude: zipEntry.lat, longitude: zipEntry.lon }
      : null;
  };

  useEffect(() => {
    if (!isHydrated || localValue.miles <= 0) return;

    const currentStored = localStorage.getItem("location_filter");
    const currentValue = currentStored ? JSON.parse(currentStored) : null;

    const valuesMatch =
      currentValue &&
      currentValue.locationType === localValue.locationType &&
      currentValue.miles === localValue.miles &&
      currentValue.zip === localValue.zip;

    if (valuesMatch) return;

    const handleUpdate = (updated) => {
      onUpdate([updated]);
      if (saveAppliedFilter) {
        localStorage.setItem("location_filter", JSON.stringify(updated));
      }
    };

    if (localValue.locationType === "default") {
      handleUpdate({
        ...localValue,
        latitude: defaultPosition.lat,
        longitude: defaultPosition.lng,
        message: `${localValue.miles} miles from default location`,
        isValid: true,
      });
    } else if (localValue.locationType === "current") {
      if (typeof window !== "undefined" && navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            handleUpdate({
              ...localValue,
              latitude,
              longitude,
              message: `${localValue.miles} miles from current location`,
              isValid: true,
            });
            localStorage.setItem(
              "geo_position",
              JSON.stringify({ lat: latitude, lng: longitude })
            );
          },
          (error) => {
            console.warn("Geolocation denied:", error.message);
            handleUpdate({
              ...localValue,
              latitude: defaultPosition.lat,
              longitude: defaultPosition.lng,
              message: `${localValue.miles} miles from default location (geolocation failed)`,
              isValid: true,
            });
          }
        );
      } else {
        console.warn("Geolocation is not available in this environment.");
        handleUpdate({
          ...localValue,
          latitude: defaultPosition.lat,
          longitude: defaultPosition.lng,
          message: `${localValue.miles} miles from default location (geolocation unavailable)`,
          isValid: true,
        });
      }
    } else if (
      localValue.locationType === "zip" &&
      localValue.zip &&
      findCoordinatesByZip(localValue.zip)
    ) {
      const coordinates = findCoordinatesByZip(localValue.zip);
      handleUpdate({
        ...localValue,
        ...coordinates,
        message: `${localValue.miles} miles from ZIP ${localValue.zip}`,
        isValid: true,
      });
    } else if (localValue.locationType === "zip" && localValue.zip) {
      onUpdate([{
        ...localValue,
        isValid: false,
        message: `Invalid ZIP code: ${localValue.zip}`,
      }]);
    }
  }, [localValue, isHydrated, defaultPosition, onUpdate, saveAppliedFilter]);

  const handleLocationTypeChange = (event) => {
    setRemoveAppliedFilter(false);
    const newType = event.target.value;
    setLocalValue((prev) => ({
      ...prev,
      locationType: newType,
      zip: "",
      latitude: null,
      longitude: null,
      message: "",
      isValid: false,
    }));
  };

  const handleZipCodeChange = (event) => {
    setLocalValue((prev) => ({ 
      ...prev, 
      zip: event.target.value,
      isValid: false,
    }));
    setRemoveAppliedFilter(false);
  };

  const generateLocationMessage = (val) => {
    if (!val || !val.miles) return "";

    switch (val.locationType) {
      case "zip":
        return val.zip ? `${val.miles} miles from ZIP ${val.zip}` : "";
      case "current":
        return `${val.miles} miles from current location`;
      case "default":
        return `${val.miles} miles from default location`;
      default:
        return "";
    }
  };

  // Debug: Show what venues would be filtered
  const getFilteredVenuesCount = () => {
    if (!localValue.isValid || !localValue.latitude || !localValue.longitude || !state?.venues) {
      return null;
    }

    const filtered = state.venues.filter(venue => {
      if (!venue.lat || !venue.lon) return false;
      
      const distance = calculateDistance(
        localValue.latitude,
        localValue.longitude,
        parseFloat(venue.lat),
        parseFloat(venue.lon)
      );
      
      return distance <= localValue.miles;
    });

    return {
      total: state.venues.length,
      filtered: filtered.length,
      distances: filtered.slice(0, 5).map(venue => ({
        name: venue.VENUE_NAME || 'Unknown',
        distance: calculateDistance(
          localValue.latitude,
          localValue.longitude,
          parseFloat(venue.lat),
          parseFloat(venue.lon)
        ).toFixed(1)
      }))
    };
  };

  const venueStats = getFilteredVenuesCount();

  return (
    <div>
      <FormControl sx={{ mb: 2 }} component="fieldset">
        <RadioGroup
          row
          value={localValue.locationType}
          onChange={handleLocationTypeChange}
        >
          <FormControlLabel
            value="default"
            control={<Radio />}
            label="Default"
          />
          <FormControlLabel
            value="current"
            control={<Radio />}
            label="Current Location"
          />
          <FormControlLabel value="zip" control={<Radio />} label="ZIP Code" />
        </RadioGroup>
      </FormControl>
      
      {isHydrated && localValue.isValid && (
        <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
          {generateLocationMessage(localValue)}
        </Typography>
      )}
      
      {localValue.locationType === "zip" && (
        <TextField
          fullWidth
          sx={{ mt: 2 }}
          label="Enter ZIP Code"
          value={localValue.zip}
          onChange={handleZipCodeChange}
          error={
            localValue.zip?.length > 0 && !findCoordinatesByZip(localValue.zip)
          }
          helperText={
            localValue.zip?.length > 0 && !findCoordinatesByZip(localValue.zip)
              ? "Invalid ZIP Code"
              : " "
          }
        />
      )}
      
      <FormControl fullWidth sx={{ mt: 1 }}>
        <Select
          value={localValue.miles}
          onChange={(e) =>
            setLocalValue((prev) => ({
              ...prev,
              miles: parseInt(e.target.value, 10),
            }))
          }
        >
          {[5, 10, 15, 20, 25, 30, 50, 100, 150, 200].map((miles) => (
            <MenuItem key={miles} value={miles}>
              {miles} miles
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      <Typography sx={{ mt: 1, fontStyle: "italic" }} gutterBottom>
        {localValue.miles} miles
      </Typography>
    </div>
  );
}

export default LocationFilter;